import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api, apiErrorMessage } from '@lipaflow/shared'
import toast from 'react-hot-toast'
import type { StepProps } from '../types'

export default function SetupWhatsAppStep({ tenantId }: StepProps) {
  const queryClient = useQueryClient()
  const [formData, setFormData] = useState({
    phone_number: '',
    phone_number_id: '',
    display_name: '',
  })

  const setupMutation = useMutation({
    mutationFn: async () => {
      await api.post('/admin/wizard/setup-whatsapp', { tenant_id: tenantId, ...formData })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-wizard', tenantId] })
      toast.success('WhatsApp configured!')
    },
    onError: (err: unknown) => {
      toast.error(apiErrorMessage(err, 'Failed to configure WhatsApp'))
    },
  })

  const skipMutation = useMutation({
    mutationFn: async () => {
      await api.post('/admin/wizard/skip-whatsapp', { tenant_id: tenantId })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-wizard', tenantId] })
      toast.success('Skipped - can configure later')
    },
    onError: (err: unknown) => {
      toast.error(apiErrorMessage(err, 'Failed to skip WhatsApp'))
    },
  })

  return (
    <div>
      <h2 className="text-xl font-semibold mb-4">Step 4: Set up WhatsApp</h2>
      <form
        onSubmit={(e) => {
          e.preventDefault()
          setupMutation.mutate()
        }}
        className="space-y-4"
      >
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-ink">Phone number</label>
            <input
              type="text"
              value={formData.phone_number}
              onChange={(e) => setFormData({ ...formData, phone_number: e.target.value })}
              placeholder="+254712345678"
              className="mt-1 block w-full border border-hairline rounded-md shadow-sm py-2 px-3"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-ink">Phone number ID (from Meta)</label>
            <input
              type="text"
              value={formData.phone_number_id}
              onChange={(e) => setFormData({ ...formData, phone_number_id: e.target.value })}
              className="mt-1 block w-full border border-hairline rounded-md shadow-sm py-2 px-3"
            />
          </div>
          <div className="col-span-2">
            <label className="block text-sm font-medium text-ink">Display name</label>
            <input
              type="text"
              value={formData.display_name}
              onChange={(e) => setFormData({ ...formData, display_name: e.target.value })}
              placeholder="Customer Name"
              className="mt-1 block w-full border border-hairline rounded-md shadow-sm py-2 px-3"
            />
          </div>
        </div>
        <div className="flex justify-between pt-4">
          <button
            type="button"
            onClick={() => skipMutation.mutate()}
            className="px-4 py-2 text-ink-muted hover:text-ink"
          >
            Skip for now
          </button>
          <button
            type="submit"
            disabled={setupMutation.isPending}
            className="px-6 py-2 bg-accent text-on-accent rounded-button hover:bg-accent-hover disabled:opacity-50"
          >
            {setupMutation.isPending ? 'Configuring...' : 'Configure WhatsApp'}
          </button>
        </div>
      </form>
    </div>
  )
}
